import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import MainLayout from "@/components/layouts/MainLayout";
import { Search, RefreshCw, Monitor, Plus, Key, Trash2 } from "lucide-react";
import { vnnoxService } from "@/lib/vnnox/vnnoxService";
import { toast } from "sonner";
import { StatusDot } from "@/components/ui/status-dot";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

interface VNNOXPlayer {
  playerId: string;
  playerType: number;
  name: string;
  sn: string;
  version: string;
  ip: string;
  lastOnlineTime: string;
  onlineStatus: number;
}

interface VNNOXCredentials {
  id: string;
  app_key: string;
  app_secret: string;
  domain: string;
  description: string;
  is_active: boolean;
}

const VNNOXPlayers: React.FC = () => {
  const { isAdmin } = useAuth();
  const [players, setPlayers] = useState<VNNOXPlayer[]>([]);
  const [credentials, setCredentials] = useState<VNNOXCredentials[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [total, setTotal] = useState(0);
  const itemsPerPage = 20;

  // Estados para el diálogo de nuevas credenciales
  const [isAddingCredentials, setIsAddingCredentials] = useState(false);
  const [newCredentials, setNewCredentials] = useState({
    appKey: "",
    appSecret: "",
    domain: "open-us.vnnox.com",
    description: ""
  });

  const fetchCredentials = async () => {
    try {
      const creds = await vnnoxService.getAllCredentials();
      setCredentials(creds);
    } catch (error) {
      console.error('Error fetching credentials:', error);
      toast.error('Error al cargar las credenciales');
    }
  };

  const fetchPlayers = async () => {
    setLoading(true);
    setError(null);
    try {
      // Primero verificamos las credenciales activas
      const activeCredentials = credentials.find(c => c.is_active);
      if (!activeCredentials) {
        throw new Error('No hay credenciales activas configuradas');
      }

      const response = await vnnoxService.getPlayers({
        start: currentPage * itemsPerPage,
        count: itemsPerPage,
        name: searchTerm || undefined
      });
      
      // Mapear los reproductores usando directamente el onlineStatus de la API
      const mappedPlayers: VNNOXPlayer[] = response.rows.map(player => ({
        playerId: player.id,
        playerType: player.type || 2, // Asíncrono por defecto
        name: player.name,
        sn: player.id,
        version: player.version || "1.0",
        ip: player.ip || "",
        lastOnlineTime: player.lastHeartbeat,
        onlineStatus: player.onlineStatus // Usar directamente el estado de la API
      }));
      
      setPlayers(mappedPlayers);
      setTotal(response.total);
      setError(null);
    } catch (err) {
      console.error('Error fetching players:', err);
      const errorMessage = err instanceof Error ? err.message : "Error al cargar los reproductores";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('VNNOXPlayers: Componente montado');
    setError(null);
    fetchCredentials().then(() => fetchPlayers());
  }, [currentPage]);

  const handleSearch = () => {
    setCurrentPage(0);
    fetchPlayers();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleAddCredentials = async () => {
    try {
      await vnnoxService.addCredentials(
        newCredentials.appKey,
        newCredentials.appSecret,
        newCredentials.domain,
        newCredentials.description
      );
      toast.success('Credenciales agregadas correctamente');
      setIsAddingCredentials(false);
      setNewCredentials({ 
        appKey: "", 
        appSecret: "", 
        domain: "open-us.vnnox.com",
        description: "" 
      });
      fetchCredentials();
    } catch (error) {
      console.error('Error adding credentials:', error);
      toast.error('Error al agregar las credenciales');
    }
  };

  const handleActivateCredentials = async (id: string) => {
    try {
      await vnnoxService.setActiveCredentials(id);
      toast.success('Credenciales activadas correctamente');
      await fetchCredentials();
      // Recargar los reproductores con las nuevas credenciales
      await fetchPlayers();
    } catch (error) {
      console.error('Error activating credentials:', error);
      toast.error('Error al activar las credenciales');
    }
  };

  const handleDeleteCredentials = async (id: string) => {
    try {
      await vnnoxService.deleteCredentials(id);
      toast.success('Credenciales eliminadas correctamente');
      fetchCredentials();
    } catch (error) {
      console.error('Error deleting credentials:', error);
      toast.error('Error al eliminar las credenciales');
    }
  };

  if (!isAdmin) {
    return (
      <MainLayout>
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">
              No tienes permisos para acceder a esta página.
            </p>
          </CardContent>
        </Card>
      </MainLayout>
    );
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return 'No disponible';
    
    // Intentar convertir el timestamp si es un número
    let date;
    if (!isNaN(Number(dateString))) {
      date = new Date(Number(dateString));
    } else {
      date = new Date(dateString);
    }
    
    if (isNaN(date.getTime())) return 'No disponible';
    
    return date.toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <MainLayout>
      {/* Panel de Resumen */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* Tarjeta de Reproductores En Línea */}
        <Card className="bg-gradient-to-br from-green-50 to-white border-green-100">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-green-100 flex items-center justify-center">
                  <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse"></div>
                </div>
                <div>
                  <p className="text-sm font-medium text-green-600">En línea</p>
                  <p className="text-2xl font-semibold">
                    {players.filter(p => p.onlineStatus === 1).length}
                  </p>
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              {players
                .filter(p => p.onlineStatus === 1)
                .map(player => (
                  <div key={player.playerId} 
                    className="text-sm px-2 py-1 rounded-md bg-white/50 flex items-center gap-2 hover:bg-white transition-colors">
                    <div className="h-1.5 w-1.5 rounded-full bg-green-500"></div>
                    <span className="truncate" title={player.name}>{player.name}</span>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>

        {/* Tarjeta de Reproductores Desconectados */}
        <Card className="bg-gradient-to-br from-red-50 to-white border-red-100">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-red-100 flex items-center justify-center">
                  <div className="h-2 w-2 rounded-full bg-red-500"></div>
                </div>
                <div>
                  <p className="text-sm font-medium text-red-600">Desconectados</p>
                  <p className="text-2xl font-semibold">
                    {players.filter(p => p.onlineStatus !== 1).length}
                  </p>
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              {players
                .filter(p => p.onlineStatus !== 1)
                .map(player => (
                  <div key={player.playerId} 
                    className="text-sm px-2 py-1 rounded-md bg-white/50 flex items-center gap-2 hover:bg-white transition-colors">
                    <div className="h-1.5 w-1.5 rounded-full bg-red-500"></div>
                    <span className="truncate" title={player.name}>{player.name}</span>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sección de Credenciales */}
      <Card className="mb-6">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle>Credenciales VNNOX</CardTitle>
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Agregar Credenciales
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Agregar Nuevas Credenciales</DialogTitle>
                <DialogDescription>
                  Ingresa las credenciales de tu cuenta VNNOX.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="appKey">App Key</Label>
                  <Input
                    id="appKey"
                    placeholder="Ingresa el App Key"
                    value={newCredentials.appKey}
                    onChange={(e) => setNewCredentials(prev => ({ ...prev, appKey: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="appSecret">App Secret</Label>
                  <Input
                    id="appSecret"
                    type="password"
                    placeholder="Ingresa el App Secret"
                    value={newCredentials.appSecret}
                    onChange={(e) => setNewCredentials(prev => ({ ...prev, appSecret: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="domain">Interface Access Domain Name</Label>
                  <Input
                    id="domain"
                    placeholder="Ejemplo: open-us.vnnox.com"
                    value={newCredentials.domain}
                    onChange={(e) => setNewCredentials(prev => ({ ...prev, domain: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Descripción</Label>
                  <Textarea
                    id="description"
                    placeholder="Agrega una descripción para identificar estas credenciales"
                    value={newCredentials.description}
                    onChange={(e) => setNewCredentials(prev => ({ ...prev, description: e.target.value }))}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsAddingCredentials(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleAddCredentials}>
                  Guardar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {credentials.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">
                No hay credenciales configuradas
              </p>
            ) : (
              credentials.map((cred) => (
                <div
                  key={cred.id}
                  className="flex items-center justify-between p-4 border rounded-lg"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Key className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{cred.description || 'Sin descripción'}</span>
                      {cred.is_active && (
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                          Activa
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">
                      <div>App Key: {cred.app_key.substring(0, 8)}...</div>
                      <div>Dominio: {cred.domain}</div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {!cred.is_active && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleActivateCredentials(cred.id)}
                      >
                        Activar
                      </Button>
                    )}
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteCredentials(cred.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Sección de Reproductores */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle>Gestión de Reproductores VNNOX</CardTitle>
          <Button
            variant="outline"
            size="icon"
            onClick={() => {
              setError(null);
              fetchPlayers();
            }}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </CardHeader>
        <CardContent>
          {/* Barra de búsqueda */}
          <div className="flex gap-2 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={handleKeyPress}
                className="pl-8"
              />
            </div>
            <Button 
              variant="secondary"
              onClick={() => {
                setError(null);
                handleSearch();
              }}
              disabled={loading}
            >
              Buscar
            </Button>
          </div>

          {error && players.length === 0 && (
            <div className="bg-destructive/10 text-destructive p-3 rounded-md mb-4">
              {error}
            </div>
          )}

          {/* Grid de tarjetas */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {loading ? (
              <div className="col-span-full text-center py-8">
                Cargando reproductores...
              </div>
            ) : players.length === 0 ? (
              <div className="col-span-full text-center py-8 text-muted-foreground">
                No se encontraron reproductores
              </div>
            ) : (
              players.map((player) => (
                <Card key={player.playerId} className="hover:bg-muted/50 transition-colors">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <StatusDot 
                            status={player.onlineStatus === 1 ? "online" : "offline"} 
                            className="h-3 w-3" // Hacer el punto un poco más grande
                          />
                          <h3 className="font-semibold text-lg truncate" title={player.name}>
                            {player.name}
                          </h3>
                        </div>
                        <p className="text-sm text-muted-foreground truncate mt-1" title={player.playerId}>
                          ID: {player.playerId}
                        </p>
                      </div>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center text-muted-foreground">
                        <Monitor className="h-4 w-4 mr-2" />
                        <span>Asíncrono</span>
                      </div>
                      {player.ip && (
                        <div className="flex items-center">
                          <span className="text-muted-foreground">IP:</span>
                          <span className="ml-2">{player.ip}</span>
                        </div>
                      )}
                      <div className="flex items-center">
                        <span className="text-muted-foreground">Última conexión:</span>
                        <span className="ml-2">{formatDate(player.lastOnlineTime)}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="text-muted-foreground">Estado:</span>
                        <span className={`ml-2 ${player.onlineStatus === 1 ? 'text-green-500 font-medium' : 'text-red-500 font-medium'}`}>
                          {player.onlineStatus === 1 ? 'En línea' : 'Desconectado'}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Paginación */}
          <div className="flex justify-between items-center mt-6">
            <span className="text-sm text-muted-foreground">
              Total: {total} reproductores
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                disabled={currentPage === 0 || loading}
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => p + 1)}
                disabled={(currentPage + 1) * itemsPerPage >= total || loading}
              >
                Siguiente
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </MainLayout>
  );
};

export default VNNOXPlayers; 